import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, openPdf } from "@/lib/api";
import { PageHeader, Label2, StatusBadge, MAINT_TONE, RESULT_TONE } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, FilePdf, SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const NORMS = ["DIN EN 13241", "ASR A1.7 / BGV", "DGUV V3 (Elektro)", "DIN 18650 (Automatiktür)", "Herstellervorgabe"];

function CreateDialog({ assets, onCreated }) {
  const [open, setOpen] = useState(false);
  const [assetId, setAssetId] = useState("");
  const [template, setTemplate] = useState("");
  const [norm, setNorm] = useState(NORMS[0]);
  const [interval, setIntervalM] = useState("12");
  const [items, setItems] = useState([{ text: "" }]);

  const submit = async () => {
    try {
      const checklist = items.filter((i) => i.text.trim()).map((i) => ({ text: i.text.trim() }));
      await api.post("/inspections", { asset_id: assetId, template_name: template, norm, interval_months: Number(interval) || 12, checklist });
      toast.success("Prüfung angelegt");
      setOpen(false); setAssetId(""); setTemplate(""); setItems([{ text: "" }]); onCreated();
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-inspection-button" className="gap-2"><Plus size={18} weight="bold" />Neue Prüfung</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="inspection-dialog">
        <DialogHeader><DialogTitle className="font-heading">Prüfung anlegen</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label2>Anlage *</Label2>
            <Select value={assetId} onValueChange={setAssetId}>
              <SelectTrigger data-testid="inspection-asset"><SelectValue placeholder="Anlage wählen" /></SelectTrigger>
              <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label2>Prüfvorlage *</Label2>
            <Input data-testid="inspection-template" placeholder="z.B. Jährliche Sicherheitsprüfung" value={template} onChange={(e) => setTemplate(e.target.value)} /></div>
          <div className="space-y-1.5"><Label2>Norm / Grundlage</Label2>
            <Select value={norm} onValueChange={setNorm}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{NORMS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label2>Intervall (Monate)</Label2><Input type="number" value={interval} onChange={(e) => setIntervalM(e.target.value)} /></div>
          <div className="space-y-2">
            <Label2>Prüfpunkte</Label2>
            {items.map((it, idx) => (
              <div key={idx} className="flex gap-2">
                <Input placeholder={`Prüfpunkt ${idx + 1}`} value={it.text} onChange={(e) => setItems((p) => p.map((x, i) => i === idx ? { text: e.target.value } : x))} />
                <Button variant="ghost" size="icon" onClick={() => setItems((p) => p.filter((_, i) => i !== idx))}><Trash size={16} /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setItems((p) => [...p, { text: "" }])}><Plus size={14} />Punkt hinzufügen</Button>
          </div>
        </div>
        <DialogFooter><Button data-testid="inspection-save" onClick={submit} disabled={!assetId || !template}>Erstellen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompleteDialog({ insp, onDone }) {
  const [open, setOpen] = useState(false);
  const [checklist, setChecklist] = useState((insp.checklist || []).map((c) => ({ ...c, result: c.result || "ok" })));
  const [note, setNote] = useState("");
  const [sig, setSig] = useState("");
  const defects = checklist.filter((c) => c.result === "mangel").map((c) => c.text);
  const overall = defects.length > 0 ? "durchgefallen" : "bestanden";

  const submit = async () => {
    try {
      await api.post(`/inspections/${insp.id}/complete`, {
        checklist, overall_result: overall, defects, note, signature_name: sig,
      });
      toast.success(`Prüfung ${overall}`, { description: overall === "bestanden" ? "Plakette ausgestellt" : "Mängel dokumentiert" });
      setOpen(false); onDone();
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="gap-1.5" data-testid={`complete-inspection-${insp.id}`}><SealCheck size={16} weight="bold" />Durchführen</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="inspection-complete-dialog">
        <DialogHeader><DialogTitle className="font-heading">Prüfung durchführen</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label2>Prüfpunkte</Label2>
            {checklist.length === 0 && <p className="text-sm text-muted-foreground">Keine Prüfpunkte hinterlegt.</p>}
            {checklist.map((c, idx) => (
              <div key={c.id || idx} className="rounded-md border border-border p-3">
                <div className="mb-2 text-sm font-medium">{c.text}</div>
                <div className="flex gap-1.5">
                  {["ok", "mangel", "na"].map((r) => (
                    <button key={r} onClick={() => setChecklist((p) => p.map((x, i) => i === idx ? { ...x, result: r } : x))}
                      className={`rounded-md border px-3 py-1 text-xs font-semibold transition-colors ${c.result === r ? (r === "ok" ? "border-success bg-success/15 text-success" : r === "mangel" ? "border-danger bg-danger/15 text-danger" : "border-border bg-muted") : "border-border text-muted-foreground hover:bg-accent"}`}>
                      {r === "ok" ? "OK" : r === "mangel" ? "Mangel" : "N/A"}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className={`rounded-md border p-3 text-sm font-semibold ${overall === "bestanden" ? "border-success/40 bg-success/10 text-success" : "border-danger/40 bg-danger/10 text-danger"}`}>
            Gesamtergebnis: {overall.toUpperCase()} {defects.length > 0 && `(${defects.length} Mängel)`}
          </div>
          <div className="space-y-1.5"><Label2>Bemerkung</Label2><Textarea value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <div className="space-y-1.5"><Label2>Digitale Unterschrift (Prüfer)</Label2><Input data-testid="inspection-signature" value={sig} onChange={(e) => setSig(e.target.value)} /></div>
        </div>
        <DialogFooter><Button data-testid="inspection-complete-save" onClick={submit} disabled={!sig}>Abschließen & Plakette</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Inspections() {
  const [items, setItems] = useState(null);
  const [assets, setAssets] = useState([]);

  const load = useCallback(() => {
    api.get("/inspections").then((r) => setItems(r.data));
    api.get("/assets").then((r) => setAssets(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!items) return <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>;

  return (
    <div>
      <PageHeader title="Prüfungen" subtitle={`${items.length} normative Prüfungen · Prüfplakette & Protokoll`}>
        <CreateDialog assets={assets} onCreated={load} />
      </PageHeader>
      <Card className="divide-y divide-border rounded-md border">
        {items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Noch keine Prüfungen.</div>}
        {items.map((i) => (
          <div key={i.id} data-testid="inspection-row" className="flex flex-col justify-between gap-3 px-5 py-4 sm:flex-row sm:items-center">
            <div className="min-w-0">
              <div className="font-semibold">{i.template_name}</div>
              <div className="text-xs text-muted-foreground">{i.asset_name} · {i.norm}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {i.overall_result && <StatusBadge tone={RESULT_TONE[i.overall_result]}>{i.overall_result}</StatusBadge>}
              <StatusBadge tone={MAINT_TONE[i.status]}>{i.status}</StatusBadge>
              {i.status !== "abgeschlossen" && <CompleteDialog insp={i} onDone={load} />}
              {i.status === "abgeschlossen" && <Button size="sm" variant="outline" className="gap-1.5" data-testid={`pdf-inspection-${i.id}`} onClick={() => openPdf(`/pdf/inspection/${i.id}`)}><FilePdf size={16} />PDF</Button>}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
